import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { apiAxios } from "../../../../utils/apiAxios";
import { GetCentralDashboardCounts, ManagerDashboardCountsDto } from "../pages/AdminDashboardCount";
import { ManagerOrderRow } from "../../../../Interfaces/MangerOrderRow";

// Use environment variable or relative path
const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "";

export const useDashboardData = () => {
  const [dashboardCounts, setDashboardCounts] = useState<ManagerDashboardCountsDto>({
    activeOrders: 0,
    awaitingResponse: 0,
    needAttention: 0,
    completed: 0,
    onTime: 0,
    cancelled: 0,
  });

  const [orders, setOrders] = useState<ManagerOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const fetchDashboardCounts = useCallback(async (): Promise<void> => {
    try {
      const res = await GetCentralDashboardCounts();
      if (res.isSuccess && res.singleData) {
        console.log(res.singleData,"dashboard count");
        setDashboardCounts(res.singleData);
      } else {
        setError("Failed to load dashboard counts: Invalid response structure");
      }
    } catch (error) {
      console.warn("Failed to load dashboard counts:", error);
      setError("Failed to load dashboard counts");
      toast.error("Failed to load dashboard counts");
    }
  }, []);

  const fetchOrders = useCallback(
    async (showLoader = false, filter?: string | null): Promise<ManagerOrderRow[]> => {
      try {
        if (showLoader) {
          setLoading(true);
          setError(null);
        }

        const finalFilter = filter ?? statusFilter;

        const url = finalFilter
          ? `${API_BASE}/Central/central/case-order-details?statusFilter=${encodeURIComponent(finalFilter)}`
          : `${API_BASE}/Central/central/case-order-details`;

        const res = await apiAxios.get(url);

        if (res.data?.isSuccess) {

          const mappedOrders: ManagerOrderRow[] = res.data.data.map((item: any, index: number) => ({
            orderId: item.orderID || `order-${index}`,
            type: item.types || "",
            state: item.states || "",
            expectedDelivery: item.expectedDelivery || "",
            expectedDeliveryDateTime: item.expectedDeliveryDateTime|| "",
            status: item.statuss || "",
            DesignerStatus: item.designerStatus || "",
            MillingStatus: item.millingStatus || "",
            caseID: item.caseID || index,
            createdOn: item.createdDate || new Date().toISOString(),
            assignedHub: item.assignedHub || "N/A",
            assignedDesigner: item.assignedDesigner || "N/A",
            designerAcceptanceDueOn: item.designerAcceptanceDueOn || "",
            millingAcceptanceDueOn: item.millingAcceptanceDueOn || "",
            designerApprovalID: item.designerApprovalID,
            millingApprovalID: item.millingApprovalID,
          }));

          setOrders(mappedOrders);
          console.log(mappedOrders,"*********Datable");
          setLastRefreshTime(new Date());
          return mappedOrders;
        } else {
          const errorMsg = res.data?.status || "Failed to load orders";
          setError(errorMsg);
          toast.error(errorMsg);
          return [];
        }
      } catch (error: any) {
        const errorMsg = error?.message || "Failed to load orders";
        setError(errorMsg);
        toast.error(errorMsg);
        return [];
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [statusFilter]
  );


  const changeStatusFilter = async (filter: string | null) => {
    setStatusFilter(filter);
    await fetchOrders(true, filter);
  };

  const refreshData = useCallback(async (showLoader = false): Promise<void> => {
    try {
      await Promise.all([
        fetchOrders(showLoader),
        fetchDashboardCounts()
      ]);
    } catch (error) {
      console.warn("Error in refreshData:", error);
    }
  }, [fetchOrders, fetchDashboardCounts]);

  // Filtered orders memoized
  const filteredOrders = useMemo(() => {
    if (!searchText.trim()) return orders;

    const text = searchText.toLowerCase();
    const filtered = orders.filter(order =>
      Object.values(order).some(value =>
        value?.toString().toLowerCase().includes(text)
      )
    );
    return filtered;
  }, [orders, searchText]);

  return {
    dashboardCounts,
    orders,
    loading,
    searchText,
    setSearchText,
    filteredOrders,
    refreshData,
    fetchOrders,
    fetchDashboardCounts,
    lastRefreshTime,
    error,
    statusFilter,
    setStatusFilter,
    changeStatusFilter
  };
};